import { FC, useCallback, useMemo } from "react";
import { useFlag } from "@unleash/proxy-client-react";
import { Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { NotificationImportantOutlined } from "@mui/icons-material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { CommitmentsDateRange } from "../options/date-range/CommitmentsDateRange";
import { PageHeader } from "../../../../common/PageHeader";
import { CommitmentTypeTabs } from "../CommitmentTypeTabs";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { ProviderIcon } from "@/components/common/ProviderIcon";
import { commitmentsNavigationItemsSelector } from "@/store/commitments/selectors/common/commitmentsNavigationItemsSelector";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { ProviderType } from "@/services/cloudchipr.api";
import { utilizationAlertsLengthSelector } from "@/store/alerts/selectors/utilization-alerts/utilizationAlertsLengthSelector";
import { CommitmentsHeaderProviderSelect } from "@/components/pages/commtiments/common/components/page-header/provider-select/CommitmentsHeaderProviderSelect";
import { getProviderName } from "@/utils/helpers/providers/getProviderName";
import { commitmentsTypeTabSelector } from "@/store/commitments/selectors/common/commitmentsTypeTabSelector";
import { commitmentPageLoadingSelector } from "@/store/commitments/selectors/common/commitmentPageLoadingSelector";
import { setCommitmentsTypeTab } from "@/store/commitments/commitmentsSlice";

interface CommitmentsPageHeaderProps {
  orgId: string;
  provider: ProviderType;
}

export const CommitmentsPageHeader: FC<CommitmentsPageHeaderProps> = ({
  orgId,
  provider,
}) => {
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector(commitmentsTypeTabSelector);
  const loading = useAppSelector(commitmentPageLoadingSelector);
  const commitmentAlertsLength = useAppSelector(
    utilizationAlertsLengthSelector,
  );

  const enableAzureCommitments = useFlag("EnableAzureCommitments");

  const { anchor, open, openMenu, closeMenu } = useMenuHook();

  const navItems = useAppSelector(commitmentsNavigationItemsSelector);

  const filteredNavItems = useMemo(() => {
    if (enableAzureCommitments) {
      return navItems;
    }
    return navItems.filter((item) => item.provider === "aws");
  }, [navItems, enableAzureCommitments]);

  const selectedOrg = useMemo(() => {
    return filteredNavItems.find(
      (item) => item.provider === provider && orgId === item.orgId,
    );
  }, [orgId, provider, filteredNavItems]);

  const commitmentItemClickHandler = useCallback(
    (currentProvider: ProviderType) => {
      if (currentProvider === "azure" && activeTab === "recommendation") {
        dispatch(setCommitmentsTypeTab("overview"));
      }
      closeMenu();
    },
    [dispatch, closeMenu, activeTab],
  );

  return (
    <PageHeader
      sticky
      loading={loading}
      actions={
        <Stack flex="1" direction="row" spacing={1}>
          {filteredNavItems.length === 1 ? (
            <Stack direction="row" alignItems="center" spacing={1} px={0.5}>
              {selectedOrg?.provider && (
                <ProviderIcon provider={selectedOrg?.provider} />
              )}
              <Typography
                color="text.secondary"
                variant="body2"
                fontWeight="medium"
              >
                {selectedOrg?.provider
                  ? getProviderName(selectedOrg?.provider, { title: true })
                  : ""}{" "}
                Commitments
              </Typography>
            </Stack>
          ) : (
            <CommitmentsHeaderProviderSelect
              provider={provider}
              selectedOrgName={selectedOrg?.org}
              open={open}
              anchor={anchor}
              openMenu={openMenu}
              closeMenu={closeMenu}
              filteredNavItems={filteredNavItems}
              onCommitmentClick={commitmentItemClickHandler}
            />
          )}
          <CommitmentTypeTabs />
        </Stack>
      }
      extra={
        activeTab !== "recommendation" && (
          <Stack
            p={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={1}
            borderColor="grey.300"
          >
            <CommitmentsDateRange />

            {commitmentAlertsLength > 0 && (
              <Button
                variant="outlined"
                component={RouterLink}
                startIcon={<NotificationImportantOutlined />}
                endIcon={<OpenInNewIcon fontSize="small" />}
                to={commitmentAlertsUrl}
                target="_blank"
              >
                {`Commitments Alerts (${commitmentAlertsLength})`}
              </Button>
            )}
          </Stack>
        )
      }
    />
  );
};

const commitmentAlertsUrl = "/alerts?alertType=utilization";
